/* eslint-env browser */

// Vendor
import { inject } from 'mobx-react';
import { AnalyticsStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { Context } from 'bernie-context';
import { ErrorBoundary } from 'bernie-view';

// Bernie
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';
import { OneKeyOnboardingWrapper } from '@shared-ui/retail-loyalty-one-key-onboarding';

// Stores
import { ContextPair, MultiContextProvider, SearchCriteriaContext } from 'stores/contexts';
import { MultiItemStore } from 'stores/multi-item-store';
import { PlatformContext } from 'src/common/stores/contexts/platformContext';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

// Components
import { Footer } from '@gc/global-components';
// Controllers
import { HotelHomeController } from 'controllers/hotel/hotel-home-controller';
import { AnimatedProgressIndicator } from 'components/common/animated-progress-indicator';
import { BodyRegion } from './body-region';
import { Header } from 'components/common/header/header';
import { InfositeController } from 'controllers/hotel/infosite-controller';
import { MesoScripts } from 'components/meso/internals/meso-scripts';
import { Notification } from 'components/common/notification';
import { PwaController } from 'controllers/admin/pwa-controller';
import { SearchController } from 'controllers/hotel/search-controller';

export type MasterPageRoute = HotelHomeController | InfositeController | PwaController | SearchController;

export interface MasterPageProps extends LocalizedComponentProps, Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  className?: string;
  context?: Context;
  'data-stid'?: string;
  page?: PageStore;
  route?: MasterPageRoute;
  searchCriteria?: SearchCriteriaModel;
  multiItem?: MultiItemStore;
  pageHeader?: string;
  experiment?: ExperimentStore;
  templateContent: React.ReactElement<Record<string, unknown>>;
}

/**
 * MasterPage - stateless component for defining a reusable page structure with specific regions of content
 *
 * @param this.props {object} - the standard this.props object passed by React
 * @returns {string} - populated HTML of all applicable regions and wrapped in a div
 */
// @ts-ignore
@withRouter
// @ts-ignore
@withLocalization
@inject('analytics', 'context', 'experiment', 'page', 'multiItem', 'searchCriteria')
export class MasterPage extends React.Component<MasterPageProps> {
  public static defaultProps = {
    l10n: null,
    templateContent: null,
  };
  static wrappedComponent: any;

  constructor(props: MasterPageProps) {
    super(props);

    if (this.props.analytics && this.props.route && this.props.route.routeData) {
      const routeData = this.props.route.routeData;

      this.props.analytics.setPageNamespace(routeData.rfrrNamespace || '');
      // nested the above if statement instead of appending to outer ones so we can start handling other routeData here

      if (this.props.page && !this.props.page.pageName) {
        this.props.page.pageName = routeData.pageName || 'page.unknown';
      }
    }

    this.setPageTitle = this.setPageTitle.bind(this);
  }

  public componentDidMount() {
    this.setPageTitle(this.props.page && this.props.page.title);
  }

  public componentWillUpdate(nextProps: MasterPageProps) {
    this.setPageTitle(nextProps.page && nextProps.page.title);
  }

  public setPageTitle(title?: string) {
    document.title = title || this.props.l10n.formatText('common.defaultPageTitle');
  }

  public render() {
    const { context, 'data-stid': dataStid, searchCriteria, pageHeader, templateContent } = this.props;

    const contextPairs: ContextPair[] = [
      { context: PlatformContext, value: context },
      { context: SearchCriteriaContext, value: searchCriteria },
    ];

    return (
      <MultiContextProvider contextPairs={contextPairs}>
        <ErrorBoundary label="lodging.masterPage.renderToNodeStreamTest">
          <Experiment name="PWA_RENDER_TO_NODE_STREAM">
            <ExperimentControl />
            <ExperimentVariant bucket={1} />
          </Experiment>
        </ErrorBoundary>
        <div data-stid={dataStid}>
          <Experiment name="Lodging_Shopping_PWA_EGTNL_AA_Test">
            <ExperimentControl />
            <ExperimentVariant bucket={1} />
          </Experiment>
          <ErrorBoundary label="lodging.masterPage.header">
            <Header />
          </ErrorBoundary>
          <ErrorBoundary label="onekey.onboarding.shopping-pwa">
            <OneKeyOnboardingWrapper />
          </ErrorBoundary>
          <main>
            <BodyRegion templateContent={templateContent} pageHeader={pageHeader} />
          </main>
          <Footer />
          <AnimatedProgressIndicator />
          <Notification />
        </div>
        <ErrorBoundary label="meso.scripts.errorboundary">
          <MesoScripts />
        </ErrorBoundary>
      </MultiContextProvider>
    );
  }
}
